import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

// Toggles the admin support composer between a public reply (emailed to the
// requester and visible in their thread) and an internal note (staff only).
// The editor itself is the shared mention-contenteditable/dropzone pair
// used by conversations, so @mentions and drag-and-drop upload behave the same
// way here as everywhere else.
export default class extends Controller<HTMLElement> {
  static targets = ["tab", "kind", "body", "hint", "submit", "container", "resolve", "moreActions"];

  declare readonly tabTargets: HTMLButtonElement[];
  declare readonly kindTarget: HTMLInputElement;
  declare readonly bodyTarget: HTMLElement;
  declare readonly hintTarget: HTMLElement;
  declare readonly submitTarget: HTMLInputElement;
  declare readonly containerTarget: HTMLElement;
  declare readonly resolveTarget: HTMLInputElement;
  declare readonly hasResolveTarget: boolean;
  declare readonly moreActionsTarget: HTMLButtonElement;
  declare readonly hasMoreActionsTarget: boolean;

  select(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const kind = button.dataset.kind === "internal" ? "internal" : "public";

    this.tabTargets.forEach((tab) => {
      tab.classList.toggle("tab-active", tab === button);
    });

    this.kindTarget.value = kind;
    this.applyKind(kind);
  }

  // Send & resolve, from the split action's dropdown. The intent rides a
  // hidden field rather than the submitter's name/value, because
  // requestSubmit() is called without a submitter — so nothing would carry it
  // otherwise. Turbo snapshots the form synchronously inside requestSubmit,
  // so resetting straight after keeps a later plain Send from resolving.
  sendAndResolve(event: Event): void {
    const form = (event.currentTarget as HTMLElement).closest("form");
    if (!form || !this.hasResolveTarget) return;

    (document.activeElement as HTMLElement | null)?.blur();
    this.resolveTarget.value = "1";
    form.requestSubmit();
    this.resolveTarget.value = "";
  }

  private applyKind(kind: string): void {
    const internal = kind === "internal";
    const suffix = internal ? "internal" : "public";

    // The editor is contenteditable, so its prompt is a data attribute rather
    // than a placeholder property.
    this.bodyTarget.dataset.placeholder = i18n.t(`support_composer.placeholder_${suffix}`);
    this.hintTarget.textContent = i18n.t(`support_composer.hint_${suffix}`);
    this.submitTarget.value = i18n.t(`support_composer.submit_${suffix}`);

    // Tint the whole card, not just the text strip: a note that is invisible to
    // the customer needs to look unmistakably different from a public reply.
    // Both background classes are managed here — leaving bg-base-100 in place
    // alongside bg-amber-50 would let stylesheet order pick the winner.
    this.containerTarget.classList.toggle("bg-amber-50", internal);
    this.containerTarget.classList.toggle("bg-base-100", !internal);
    this.containerTarget.classList.toggle("border-amber-300", internal);
    this.containerTarget.classList.toggle("border-base-300", !internal);
    this.hintTarget.classList.toggle("text-amber-700", internal);

    // An internal note never resolves a ticket. The arrow disables rather than
    // hides so the split keeps its shape — a disabled trigger can't take focus,
    // which is what keeps the menu closed.
    if (this.hasMoreActionsTarget) {
      this.moreActionsTarget.disabled = internal;
    }
  }
}
